import React, { useContext, useEffect, useState } from 'react';
import { Box, CircularProgress, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../providers/UserProvider';
import {
    StyledColumnBox,
    StyledGreenButton,
    StyledMediumTypography,
    StyledModalButtons,
    StyledNormalTypography,
    StyledWhiteButton
} from '../theme/StyledElements';
import { TimeComponent } from '../components/Collections/TimeComponent';
import { DateComponent } from '../components/Collections/DateComponent';
import { DateCollection } from '../models/DateCollection';
import { TimeCollection } from '../models/TimeCollection';
import { getProducer, updateProducer } from '../services/cloudFirestore';
import { useLocation, useNavigate } from 'react-router-dom';
import { SelectTime } from '../models/SelectTime';
import { TimeCollectionData } from '../components/Collections/TimeCollectionData';
import { Days } from '../models/Days';
import { useMountedRef } from '../hooks/useMountedRef';

const StyledCollectionBox = styled(StyledColumnBox)(() => ({
    width: '430px',
    marginBottom: '80px',
    marginTop: '40px',
    marginLeft: '20px'
}));

export function Collection(): React.ReactElement {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const mounted = useMountedRef();
    const { t } = useTranslation();
    const user = useContext(UserContext);
    const [modify, setModify] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [time, setTime] = useState<TimeCollection>({
        temps: '4h',
        disponible: false
    });
    const [date, setDate] = useState<DateCollection>({
        date: new Date(),
        disponible: false
    });
    const [lundi, setLundi] = useState<SelectTime[]>();
    const [mardi, setMardi] = useState<SelectTime[]>();
    const [mercredi, setMercredi] = useState<SelectTime[]>();
    const [jeudi, setJeudi] = useState<SelectTime[]>();
    const [vendredi, setVendredi] = useState<SelectTime[]>();
    const [samedi, setSamedi] = useState<SelectTime[]>();
    const [dimanche, setDimanche] = useState<SelectTime[]>();

    useEffect(() => {
        if (pathname) {
            if (pathname.split('/')[2] === 'edit') {
                setModify(true);
            }
            if (pathname.split('/')[2] === 'view') {
                setModify(false);
            }
        }
    }, [pathname]);

    useEffect(() => {
        if (user && user.idProducteur && mounted.current) {
            getProducer(user.idProducteur).then((updatedUser) => {
                if (updatedUser && mounted.current) {
                    const content = updatedUser.collections;
                    if (content && mounted.current) {
                        setTime({
                            temps: content.limiteDeTemps.temps,
                            disponible: content.limiteDeTemps.disponible
                        });
                        setDate({
                            date: content.dernierRendezVous.date,
                            disponible: content.dernierRendezVous.disponible
                        });
                        setLundi(content.mesHorairesDeCollecte?.lundi);
                        setMardi(content.mesHorairesDeCollecte?.mardi);
                        setMercredi(content.mesHorairesDeCollecte?.mercredi);
                        setJeudi(content.mesHorairesDeCollecte?.jeudi);
                        setVendredi(content.mesHorairesDeCollecte?.vendredi);
                        setSamedi(content.mesHorairesDeCollecte?.samedi);
                        setDimanche(content.mesHorairesDeCollecte?.dimanche);
                    }
                }
            });
        }
    }, []);

    const onTimeFieldChange = (state: string, value: string | boolean): void => {
        setTime({ ...time, [state]: value });
    };

    const onDateFieldChange = (state: string, value: string | boolean): void => {
        setDate({ ...date, [state]: value });
    };

    const publishCollections = async (): Promise<void> => {
        const data = {
            mesHorairesDeCollecte: {
                lundi,
                mardi,
                mercredi,
                jeudi,
                vendredi,
                samedi,
                dimanche
            },
            limiteDeTemps: time,
            dernierRendezVous: date
        };
        if (user && user.idProducteur) {
            setLoading(true);
            await updateProducer(user.idProducteur, data);
            setLoading(false);
            navigate('/collection/view');
        }
    };

    return (
        <StyledCollectionBox>
            <StyledNormalTypography variant="h2">{t('myCollection')}</StyledNormalTypography>
            <Box style={{ marginTop: '20px' }}>
                <Box>
                    <TimeCollectionData
                        modify={modify}
                        title={t('monday')}
                        timing={lundi}
                        onDataChange={(value: SelectTime[]): void => {
                            setLundi(value);
                        }}
                    />
                    <TimeCollectionData
                        modify={modify}
                        title={t('tuesday')}
                        timing={mardi}
                        onDataChange={(value: SelectTime[]): void => {
                            setMardi(value);
                        }}
                    />
                    <TimeCollectionData
                        modify={modify}
                        title={t('wednesday')}
                        timing={mercredi}
                        onDataChange={(value: SelectTime[]): void => {
                            setMercredi(value);
                        }}
                    />
                    <TimeCollectionData
                        modify={modify}
                        title={t('thursday')}
                        timing={jeudi}
                        onDataChange={(value: SelectTime[]): void => {
                            setJeudi(value);
                        }}
                    />
                    <TimeCollectionData
                        modify={modify}
                        title={t('friday')}
                        timing={vendredi}
                        onDataChange={(value: SelectTime[]): void => {
                            setVendredi(value);
                        }}
                    />
                    <TimeCollectionData
                        modify={modify}
                        title={t('saturday')}
                        timing={samedi}
                        onDataChange={(value: SelectTime[]): void => {
                            setSamedi(value);
                        }}
                    />
                    <TimeCollectionData
                        modify={modify}
                        title={t('sunday')}
                        timing={dimanche}
                        onDataChange={(value: SelectTime[]): void => {
                            setDimanche(value);
                        }}
                    />
                </Box>
                <StyledNormalTypography
                    variant="h2"
                    sx={{ marginTop: '40px', marginBottom: '20px' }}>
                    {t('deadlineBeforeCollection')}
                </StyledNormalTypography>
                <TimeComponent
                    modify={modify}
                    value={time.temps}
                    checked={time.disponible}
                    onTimeChange={(value: string): void => {
                        onTimeFieldChange('temps', value);
                    }}
                    onCheckedChange={(value: boolean): void => {
                        onTimeFieldChange('disponible', value);
                    }}
                />
                <StyledMediumTypography variant="h4" sx={{ margin: '20px 0px' }}>
                    {t('timeForPreparationOfBasket')}
                </StyledMediumTypography>
                <StyledNormalTypography variant="subtitle2">
                    {t('timeRequiredToPrepareCustomerOrder')}
                </StyledNormalTypography>
                <DateComponent
                    modify={modify}
                    value={date.date}
                    checked={date.disponible}
                    onDateChange={(value: string): void => {
                        onDateFieldChange('date', value);
                    }}
                    onCheckedChange={(value: boolean): void => {
                        onDateFieldChange('disponible', value);
                    }}
                />
                <StyledMediumTypography variant="h4" sx={{ margin: '20px 0px' }}>
                    {t('dateOfBasketAvailability')}
                </StyledMediumTypography>
                <StyledNormalTypography variant="subtitle2">
                    {t('dateCustomerOrderWillBeReady')}
                </StyledNormalTypography>
            </Box>
            <StyledModalButtons sx={{ justifyContent: 'flex-end', margin: '20px 0px 20px 0px' }}>
                {modify && (
                    <StyledWhiteButton onClick={(): void => navigate('/collection/view')}>
                        <StyledMediumTypography variant="caption">
                            {t('cancel')}
                        </StyledMediumTypography>
                    </StyledWhiteButton>
                )}
                {loading && (
                    <CircularProgress
                        style={{
                            color: '#134C03',
                            height: '25px',
                            width: '25px',
                            marginTop: '25px'
                        }}
                    />
                )}
                {!loading && (
                    <StyledGreenButton
                        onClick={(): void => {
                            modify ? publishCollections() : navigate('/collection/edit');
                        }}>
                        <StyledMediumTypography
                            variant="caption"
                            color="#FFFFFF"
                            sx={{ textTransform: 'uppercase' }}>
                            {modify ? t('save') : t('changeTimesAndTiming')}
                        </StyledMediumTypography>
                    </StyledGreenButton>
                )}
            </StyledModalButtons>
        </StyledCollectionBox>
    );
}
